import { OldEvent } from "./types/WBEvent";
import { WBEventOuterServer, WBEventOuter } from "./types/WBEvent2";
import { UserInfo } from "./types/UserInfo";
import KefirBus from "./KefirBus";
import { Stream } from "kefir";
// import Mousetrap from "mousetrap";

type OmniServerEvent = WBEventOuterServer | OldEvent;

export interface WebsocketControllerDelegate {
  websocketDidHaveError: (e: Event) => void;
  websocketDidResolveError: () => void;
  websocketDidChangeUsers: (users: Record<string, UserInfo>) => void;
  getUsername: () => string;
  getColor: () => string;
}

export class WebsocketController {
  username: string | null = null;
  websocket: WebSocket | null = null;
  hasLoadedEvents = false;
  isDead = false;
  queue: WBEventOuter[] = [];
  lastEventTimestampSeen = 0;
  users: Record<string, UserInfo> = {};

  private _onlineBus = new KefirBus<boolean, void>(
    "WebsocketController.isOnline"
  );
  get onlineStream(): Stream<boolean, void> {
    return this._onlineBus.stream;
  }

  private eventBus = new KefirBus<OmniServerEvent, void>(
    "WebsocketController.eventStream"
  );
  get eventStream(): Stream<OmniServerEvent, void> {
    return this.eventBus.stream;
  }

  private resolveLoadingPromise?: () => void;
  private rejectLoadingPromise?: (error: string) => void;
  loadingPromise = new Promise<void>((resolve, reject) => {
    this.resolveLoadingPromise = resolve;
    this.rejectLoadingPromise = reject;
  });

  constructor(
    private websocketServerURL: string,
    private wbid: string,
    private clientID: string,
    private accessKey: string,
    private delegate: WebsocketControllerDelegate
  ) {}

  get maySend() {
    return (
      !this.isDead &&
      this.websocket &&
      this.websocket.readyState === WebSocket.OPEN
    );
  }

  teardown() {
    this.isDead = true;
    if (this.websocket?.readyState != WebSocket.OPEN) {
      this.websocket?.close();
      return;
    }
    this.websocket?.close();
  }

  get disableWebsocket(): boolean {
    return (
      this.wbid === "demo" // || !window.wbconfig.feature_flag.websocket_server
    );
  }

  publish(event: WBEventOuter) {
    try {
      this.websocket?.send(JSON.stringify(event));
    } catch (e) {
      console.error(e);
      this.queue.push(event);
    }
  }

  forceReconnect() {
    this.websocket?.close();
  }

  connect(isReconnect = false) {
    if (this.disableWebsocket) {
      if (this.resolveLoadingPromise) this.resolveLoadingPromise();
      return;
    }

    if (isReconnect) {
      console.log("Reconnecting to websocket");
    }
    if (this.websocket) return;
    if (!this.wbid) {
      throw new Error("Trying to connect to websocket but have no wbid");
    }

    this.websocket = new WebSocket(this.websocketServerURL);

    this.websocket.onopen = () => {
      if (this.isDead) {
        this.websocket?.close();
      }

      this._onlineBus.emit(false);

      this.websocket?.send(
        JSON.stringify({
          verb: "subscribe",
          wbid: this.wbid,
          clientID: this.clientID,
          accessKey: this.accessKey,
          username: this.delegate.getUsername(),
          color: this.delegate.getColor(),
          lastEventTimestampSeen: this.lastEventTimestampSeen,
        })
      );
      const queue = this.queue;
      this.queue = [];
      if (this.queue.length)
        console.log("Sending", queue.length, "queued events");
      queue.forEach((e) => this.publish(e));
      if (this.queue.length) console.log("Sent", queue.length, "queued events");
    };

    this.websocket.onmessage = (e) => {
      this.delegate?.websocketDidResolveError();

      const msg = JSON.parse(e.data);

      if (this.handleSignoff(msg)) return;
      if (this.handleUpsertUser(msg)) return;

      this.hasLoadedEvents = true;

      // Server might send us an array of events instead of a single one,
      // specifically at page load
      if (Array.isArray(msg) && (msg as OmniServerEvent[])) {
        // if (window.wbconfig.debug && msg.length) {
        //   console.profile("Load");
        // }

        let minTimestamp = Date.now();
        let maxTimestamp = 0;
        let i = 0;
        console.time("FirstUpdate");
        // let lastEvent: unknown = null;

        const seenSubjectIDs: Record<string, number> = {};

        const reportEvent = (singleEvent: OmniServerEvent) => {
          minTimestamp = Math.min(singleEvent.timestamp, minTimestamp);
          maxTimestamp = Math.max(singleEvent.timestamp, minTimestamp);
          if (!seenSubjectIDs[singleEvent.clientID]) {
            // console.log(
            //   "Hello",
            //   singleEvent.clientID,
            //   "at",
            //   singleEvent.timestamp
            // );
            seenSubjectIDs[singleEvent.clientID] = singleEvent.timestamp;
          }
          this.handleEvent(singleEvent as OmniServerEvent);
        };

        for (const singleEvent of msg) {
          i += 1;
          // if (i >= 580) {
          //   console.log(singleEvent.payload);
          // }
          if (i % 500 === 0) console.log(`${i}/${msg.length}`);
          reportEvent(singleEvent);
          // lastEvent = singleEvent;
        }
        // let remainingEvents = msg.slice(580, msg.length);
        // // Mousetrap.bind("right", () => {
        // //   if (remainingEvents.length > 0) {
        // //     const e = remainingEvents[0];
        // //     remainingEvents = remainingEvents.slice(1, remainingEvents.length);
        // //     console.log(JSON.stringify(e, undefined, 2));
        // //     reportEvent(e);
        // //   }
        // // });
        console.timeEnd("FirstUpdate");
        // console.log(JSON.stringify(lastEvent, undefined, 2));
        if (msg.length > 0) {
          console.log(
            "Received",
            msg.length,
            "events from",
            minTimestamp,
            "to",
            maxTimestamp
          );
        } else {
          console.log("Received no events");
        }

        // if (window.wbconfig.debug && msg.length) {
        //   console.profileEnd("Load");
        // }
      } else {
        this.handleEvent(msg as OmniServerEvent);
      }

      // Server sent us some events, so resolve loading promise if we haven't already
      if (this.resolveLoadingPromise) this.resolveLoadingPromise();
      this.resolveLoadingPromise = undefined;
    };

    this.websocket.onclose = (e) => {
      if (this.isDead) return;
      this._onlineBus.emit(true);
      console.log(
        "Socket is closed. Reconnect will be attempted in 1 second.",
        e.reason
      );
      this.websocket = null;
      setTimeout(() => this.connect(true), 1000);
    };

    this.websocket.onerror = (e) => {
      console.error("Socket encountered error: ", e, "Closing socket");
      this.websocket?.close();
      this.delegate?.websocketDidHaveError(e);
    };
  }

  private handleSignoff(msg: { verb: string; clientID: string }): boolean {
    if (msg.verb !== "signoff") return false;
    delete this.users[msg.clientID as string];
    this.delegate.websocketDidChangeUsers(this.users);
    return true;
  }

  private handleUpsertUser(msg: UpsertUserMessage): boolean {
    // Special case: upsert-user
    if (msg.verb !== "upsert-user") return false;
    if (msg.clientID === this.clientID) return true;
    this.users[msg.clientID] = {
      username: msg.username as string,
      color: msg.color as string,
    };
    this.delegate.websocketDidChangeUsers(this.users);
    return true;
  }

  private handleEvent(msg: OmniServerEvent) {
    if (msg.timestamp) {
      this.lastEventTimestampSeen = Math.max(
        this.lastEventTimestampSeen,
        msg.timestamp
      );
      if (isNaN(this.lastEventTimestampSeen)) debugger;
    }
    this.eventBus.emit(msg);
  }

  refreshUserData() {
    if (!this.maySend) return;
    this.websocket?.send(
      JSON.stringify({
        verb: "upsert-user",
        wbid: this.wbid,
        clientID: this.clientID,
        username: this.delegate.getUsername(),
        color: this.delegate.getColor(),
      })
    );
  }
}

interface UpsertUserMessage {
  verb: "upsert-user";
  wbid: string;
  clientID: string;
  username: string;
  color: string;
}
